import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowIcon from '../../components/ArrowIcon';
import FooterBase from '../../components/Footer';
import { ServiceIcon, SERVICE_ICON_SLUGS } from '../../data/serviceIcons';

/* ══════════════════════════════════
   HOOKS
══════════════════════════════════ */
export function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.07 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useRevealOnce(threshold = 0.08) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useCountUp(target, active, duration = 1500, isFloat = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf, start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = eased * target;
      setVal(isFloat ? Math.round(cur * 10) / 10 : Math.round(cur));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration, isFloat]);
  return val;
}

function parseStatVal(val) {
  const str = String(val).trim();
  let prefix = '';
  let rest = str;
  if (rest[0] === '$' || rest[0] === '₹') { prefix = rest[0]; rest = rest.slice(1); }
  const m = rest.match(/^(\d+(?:\.\d+)?)(.*)/);
  if (!m) return { prefix, num: 0, suffix: str, canAnim: false, isFloat: false };
  return { prefix, num: parseFloat(m[1]), suffix: m[2], canAnim: true, isFloat: m[1].includes('.') };
}

/* ══════════════════════════════════
   IoT NETWORK VISUALIZATION (hero right panel)
══════════════════════════════════ */
function IoTNetworkViz({ color, visible }) {
  const W = 380, H = 380, cx = 190, cy = 190, R = 118;
  const angles = [-90, -30, 30, 90, 150, 210];
  const nodes = angles.map(deg => {
    const rad = deg * Math.PI / 180;
    return { x: cx + R * Math.cos(rad), y: cy + R * Math.sin(rad) };
  });

  /* six small icons inside peripheral nodes */
  const nodeIcons = [
    <path d="M-6-2A8 8 0 0 1 6-2M-4 1A5 5 0 0 1 4 1M-1.5 4A1.8 1.8 0 0 1 1.5 4" stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round"/>,
    <path d="M-5 0A4 4 0 0 1 3-3A3 3 0 0 1 6 0H-5Z" stroke={color} strokeWidth="1.3" fill={`${color}20`} strokeLinejoin="round"/>,
    <rect x="-3.5" y="-6" width="7" height="12" rx="1.5" stroke={color} strokeWidth="1.3" fill="none"/>,
    <g stroke={color} strokeWidth="1.4" strokeLinecap="round"><line x1="-4" y1="4" x2="-4" y2="-4"/><line x1="0" y1="4" x2="0" y2="-7"/><line x1="4" y1="4" x2="4" y2="0"/></g>,
    <rect x="-4" y="-4" width="8" height="8" rx="1.5" stroke={color} strokeWidth="1.3" fill="none"/>,
    <path d="M0-6A4 4 0 0 1 4-2C4 1 5 2 6 3H-6C-5 2-4 1-4-2A4 4 0 0 1 0-6Z M-1.5 5A1.8 1.8 0 0 0 1.5 5" stroke={color} strokeWidth="1.3" fill="none"/>,
  ];

  /* center hub icon — IoT circuit */
  const hubIcon = (
    <g transform={`translate(${cx-14},${cy-14})`}>
      <circle cx="14" cy="14" r="4" stroke={color} strokeWidth="1.6" fill={`${color}30`}/>
      <path d="M14 4v4M14 20v4M4 14h4M20 14h4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M7.1 7.1l2.8 2.8M14.1 14.1l2.8 2.8M7.1 20.9l2.8-2.8M14.1 9.9l2.8-2.8" stroke={color} strokeWidth="1.1" strokeLinecap="round" opacity="0.65"/>
    </g>
  );

  return (
    <div style={{
      position: 'relative', width: W, height: H,
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'scale(0.88) translateY(32px)',
      transition: 'opacity 900ms 300ms cubic-bezier(.2,.8,.2,1), transform 900ms 300ms cubic-bezier(.2,.8,.2,1)',
    }}>
      {/* Background ambient glow */}
      <div style={{
        position: 'absolute', inset: -40,
        background: `radial-gradient(circle at 50% 50%, ${color}18 0%, transparent 65%)`,
        borderRadius: '50%', pointerEvents: 'none',
      }}/>

      <svg width={W} height={H} style={{ position: 'relative', zIndex: 1, overflow: 'visible' }}>
        <defs>
          {nodes.map((n, i) => (
            <path key={i} id={`ivpath${i}`} d={`M ${cx} ${cy} L ${n.x} ${n.y}`}/>
          ))}
          <filter id="ivglow">
            <feGaussianBlur stdDeviation="2.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id={`ivhub`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity="0.18"/>
            <stop offset="100%" stopColor={color} stopOpacity="0.04"/>
          </radialGradient>
        </defs>

        {/* Concentric decorative rings */}
        <circle cx={cx} cy={cy} r={48} stroke={color} strokeOpacity="0.10" strokeWidth="1" fill="none"/>
        <circle cx={cx} cy={cy} r={80} stroke={color} strokeOpacity="0.07" strokeWidth="1" fill="none"/>
        <circle cx={cx} cy={cy} r={R + 10} stroke={color} strokeOpacity="0.05" strokeWidth="1" fill="none"/>

        {/* Connection lines — dashed, animated */}
        {nodes.map((n, i) => (
          <line key={i}
            x1={cx} y1={cy} x2={n.x} y2={n.y}
            stroke={color} strokeOpacity="0.22" strokeWidth="1.5"
            strokeDasharray="5 4"
            style={{ animation: `indFlow 1.8s linear ${i * 0.3}s infinite` }}
          />
        ))}

        {/* Traveling data packets */}
        {nodes.map((_, i) => (
          <circle key={`p${i}`} r="3.5" fill={color} filter="url(#ivglow)">
            <animateMotion dur={`${1.6 + i * 0.22}s`} repeatCount="indefinite" begin={`${i * 0.32}s`}>
              <mpath href={`#ivpath${i}`}/>
            </animateMotion>
          </circle>
        ))}

        {/* Peripheral nodes */}
        {nodes.map((n, i) => (
          <g key={`n${i}`}>
            <circle cx={n.x} cy={n.y} r="26" fill={`url(#ivhub)`} stroke={color} strokeOpacity="0.20" strokeWidth="1.2"
              style={{ animation: `indNodePulse ${4 + i * 0.5}s ease-in-out ${i * 0.6}s infinite` }}/>
            <circle cx={n.x} cy={n.y} r="20" fill={`${color}0A`} stroke={color} strokeOpacity="0.10" strokeWidth="1" fill="none"/>
            <g transform={`translate(${n.x},${n.y})`}>{nodeIcons[i]}</g>
          </g>
        ))}

        {/* Center hub */}
        <rect x={cx-50} y={cy-50} width="100" height="100" rx="20" fill={`${color}14`} stroke={color} strokeOpacity="0.40" strokeWidth="1.5"/>
        <rect x={cx-44} y={cy-44} width="88" height="88" rx="17" fill={`${color}08`} stroke={color} strokeOpacity="0.08" strokeWidth="1"/>
        {hubIcon}
      </svg>
    </div>
  );
}

/* ══════════════════════════════════
   INDUSTRY HERO — 2-column with IoT viz
══════════════════════════════════ */
export function IndustryHero({ tag, breadcrumb, title, titleAccent, subtitle, color, visible, visual }) {
  return (
    <section style={{ background: 'transparent', paddingTop: 120, paddingBottom: 80, position: 'relative', overflow: 'hidden' }}>
      {/* Background dotted grid */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(rgba(10,10,20,0.028) 1px, transparent 1px)', backgroundSize: '28px 28px' }}/>
      {/* Color orbs */}
      <div style={{ position: 'absolute', top: -140, right: '2%', width: 640, height: 640, borderRadius: '50%', background: `radial-gradient(circle, ${color}12 0%, transparent 58%)`, pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', bottom: -80, left: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,60,255,0.06) 0%, transparent 65%)', pointerEvents: 'none' }}/>
      {/* Floating secondary orb */}
      <div style={{ position: 'absolute', top: '40%', left: '38%', width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${color}08 0%, transparent 70%)`, pointerEvents: 'none', animation: 'indHeroOrb 9s ease-in-out infinite' }}/>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)', position: 'relative', zIndex: 1 }}>
        <div className="ind-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 'clamp(32px,5vw,72px)', alignItems: 'center' }}>

          {/* LEFT — content */}
          <div>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(14px)', transition: 'opacity 480ms, transform 480ms' }}>
              <Link to="/" style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', letterSpacing: '0.04em', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase' }}>Home</Link>
              <span style={{ color: 'rgba(10,10,20,0.22)', fontSize: 11 }}>/</span>
              <Link to="/sectors" style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', letterSpacing: '0.04em', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase' }}>Industries</Link>
              <span style={{ color: 'rgba(10,10,20,0.22)', fontSize: 11 }}>/</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', letterSpacing: '0.04em', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase' }}>{breadcrumb}</span>
            </div>

            {/* Tag chip */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', background: `${color}0F`, border: `1px solid ${color}2E`, borderRadius: 999, marginBottom: 24, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(14px)', transition: 'opacity 480ms 60ms, transform 480ms 60ms' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }}/>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color, fontFamily: "'Inter', sans-serif" }}>{tag}</span>
            </div>

            {/* Headline */}
            <h1 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(32px,4.8vw,60px)', lineHeight: 1.07, color: 'var(--ink)', marginBottom: 20, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(22px)', transition: 'opacity 520ms 120ms, transform 520ms 120ms' }}>
              {title}{' '}
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color }}>{titleAccent}</span>
            </h1>

            {/* Subtitle */}
            <p style={{ fontSize: 'clamp(14px,1.4vw,16px)', lineHeight: 1.84, color: 'var(--ink-soft)', maxWidth: 500, marginBottom: 36, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity 520ms 180ms, transform 520ms 180ms' }}>
              {subtitle}
            </p>

            {/* CTA buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 48, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(14px)', transition: 'opacity 520ms 250ms, transform 520ms 250ms' }}>
              <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '13px 26px', background: `linear-gradient(135deg,${color},${color}CC)`, color: 'white', borderRadius: 999, fontSize: 13, fontWeight: 700,  boxShadow: `0 10px 28px ${color}44` }}>
                Discuss Your Project <ArrowIcon size={12}/>
              </Link>
              <Link to="/iot-solutions" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '13px 26px', background: 'rgba(255,255,255,0.82)', border: '1.5px solid rgba(10,10,20,0.12)', color: 'var(--ink)', borderRadius: 999, fontSize: 13, fontWeight: 600,  }}>
                All Services <ArrowIcon size={12}/>
              </Link>
            </div>

            {/* Mini feature pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, opacity: visible ? 1 : 0, transition: 'opacity 520ms 340ms' }}>
              {['End-to-end delivery', 'Indian market expertise', 'Hardware + Software'].map((feat, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(10,10,20,0.04)', border: '1px solid rgba(10,10,20,0.08)', borderRadius: 999, fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
                  <svg width="7" height="7" viewBox="0 0 7 7"><circle cx="3.5" cy="3.5" r="3.5" fill={color} opacity="0.7"/></svg>
                  {feat}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — visualization */}
          <div className="ind-hero-viz" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {visual ? (
              <img src={visual} alt="" style={{ width:'100%', display:'block', opacity:visible?1:0, transform:visible?'none':'translateY(24px)', transition:'opacity 700ms 300ms, transform 700ms 300ms' }} />
            ) : (
              <IoTNetworkViz color={color} visible={visible}/>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════
   INTRODUCTION
══════════════════════════════════ */
export function IndustryIntro({ heading, body, color }) {
  const [ref, visible] = useRevealOnce();
  return (
    <section style={{ padding: '0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} className="ind-intro-grid" style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr',
          gap: 'clamp(24px,4vw,56px)',
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(10,10,20,0.08)',
          borderRadius: 28, padding: 'clamp(28px,4vw,52px)',
          position: 'relative', overflow: 'hidden',
          opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)',
          transition: 'opacity 560ms, transform 560ms',
        }}>
          {/* Decorative corner blob */}
          <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${color}0C 0%, transparent 70%)`, pointerEvents: 'none' }}/>

          {/* Left accent */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 16, minWidth: 0 }}>
            <div style={{ width: 4, height: '100%', minHeight: 80, background: `linear-gradient(180deg, ${color} 0%, ${color}40 100%)`, borderRadius: 4 }}/>
          </div>

          {/* Right content */}
          <div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color, textTransform: 'uppercase' }}>WHY IT MATTERS</span>
            <h2 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(22px,3vw,38px)', lineHeight: 1.1, marginTop: 10, marginBottom: 20, color: 'var(--ink)' }}>{heading}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {body.map((para, i) => (
                <p key={i} style={{ fontSize: 15, lineHeight: 1.84, color: 'var(--ink-soft)', margin: 0, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(12px)', transition: `opacity 460ms ${i * 120 + 160}ms, transform 460ms ${i * 120 + 160}ms` }}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) { .ind-intro-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

/* ══════════════════════════════════
   USE CASES GRID
══════════════════════════════════ */
export function UseCasesGrid({ cases, color }) {
  const [headRef, headVisible] = useRevealOnce();
  return (
    <section style={{ background: 'transparent', padding: 'clamp(48px,6vw,80px) 0' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>
        {/* Section header */}
        <div ref={headRef} style={{ marginBottom: 44, opacity: headVisible ? 1 : 0, transform: headVisible ? 'none' : 'translateY(20px)', transition: 'opacity 520ms, transform 520ms' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color, textTransform: 'uppercase' }}>USE CASES</span>
          <h2 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(28px,4vw,52px)', lineHeight: 1.07, marginTop: 8, marginBottom: 10 }}>
            What We{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color }}>Build</span>
          </h2>
          {/* Animated underline */}
          <div style={{ width: headVisible ? 56 : 0, height: 3, background: color, borderRadius: 2, marginTop: 4, transition: 'width 600ms 200ms cubic-bezier(.2,.8,.2,1)', opacity: 0.75 }}/>
        </div>

        {/* 2×3 card grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }} className="use-cases-grid">
          {cases.map((uc, i) => (
            <UseCaseCard key={uc.title} uc={uc} idx={i} color={color}/>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 860px) { .use-cases-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 540px) { .use-cases-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function UseCaseCard({ uc, idx, color }) {
  const [cardRef, cardVisible] = useRevealOnce(0.05);
  const [hov, setHov] = useState(false);
  const Wrap = uc.to ? Link : 'div';
  const wrapProps = uc.to ? { to: uc.to, className: 'hoverable' } : {};
  const slug = uc.to ? uc.to.split('/').pop() : null;
  const hasServiceIcon = slug && SERVICE_ICON_SLUGS.has(slug);

  return (
    <Wrap
      ref={cardRef}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      {...wrapProps}
      style={{
        position: 'relative', overflow: 'hidden',
        display: 'block', textDecoration: 'none',
        padding: 'clamp(20px,2.4vw,28px)',
        background: hov ? `${color}09` : 'rgba(255,255,255,0.8)',
        border: `1px solid ${hov ? color + '36' : 'rgba(10,10,20,0.08)'}`,
        borderRadius: 20,
        transition: 'background 260ms ease, border-color 260ms ease, box-shadow 260ms ease, transform 260ms ease',
        transform: hov ? 'translateY(-6px)' : (cardVisible ? 'none' : 'translateY(24px)'),
        opacity: cardVisible ? 1 : 0,
        transitionDelay: `${idx * 60}ms`,
        boxShadow: hov ? `0 16px 40px ${color}18` : '0 2px 8px rgba(10,10,20,0.04)',
        cursor: uc.to ? 'pointer' : 'default',
      }}
    >
      {/* Left accent bar (reveals on hover) */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: `linear-gradient(180deg, ${color} 0%, ${color}60 100%)`,
        borderRadius: '20px 0 0 20px',
        opacity: hov ? 1 : 0, transition: 'opacity 240ms',
      }}/>

      {/* Top-right number badge */}
      <div style={{
        position: 'absolute', top: 14, right: 14,
        fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 22,
        color, opacity: hov ? 0.20 : 0.10,
        lineHeight: 1, transition: 'opacity 260ms', userSelect: 'none',
      }}>
        {String(idx + 1).padStart(2, '0')}
      </div>

      {/* Dot pattern on hover */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 20,
        backgroundImage: `radial-gradient(${color}14 1px, transparent 1px)`,
        backgroundSize: '18px 18px',
        opacity: hov ? 1 : 0, transition: 'opacity 300ms', pointerEvents: 'none',
      }}/>

      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 14,
        background: hov ? `${color}18` : `${color}0E`,
        border: `1px solid ${hov ? color + '30' : color + '18'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16,
        transition: 'background 260ms, border-color 260ms, transform 260ms',
        transform: hov ? 'scale(1.08)' : 'scale(1)',
        position: 'relative', zIndex: 1,
      }}>
        {hasServiceIcon ? <ServiceIcon slug={slug} color={color}/> : <UseCaseIcon icon={uc.icon} color={color}/>}
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.32 }}>{uc.title}</div>
          {uc.to && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: hov ? 1 : 0.4, transform: hov ? 'translate(2px,-2px)' : 'none', transition: 'opacity 220ms, transform 220ms' }}>
              <path d="M3.5 10.5l7-7M6 3.5h4.5V8" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.74, color: 'var(--ink-soft)' }}>{uc.desc}</div>
      </div>
    </Wrap>
  );
}

/* ══════════════════════════════════
   STATS — count-up animation
══════════════════════════════════ */
export function IndustryStats({ stats, color }) {
  const [ref, visible] = useRevealOnce(0.15);
  return (
    <section style={{ background: 'transparent', padding: 'clamp(40px,5vw,72px) 0' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>

        {/* Section header */}
        <div ref={ref} style={{ marginBottom: 40, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)', transition: 'opacity 520ms, transform 520ms' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color, textTransform: 'uppercase' }}>THE NUMBERS SPEAK</span>
          <h2 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(24px,3.5vw,44px)', lineHeight: 1.07, marginTop: 8, marginBottom: 0, color: 'var(--ink)' }}>
            Industry{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color }}>Impact</span>
          </h2>
        </div>

        {/* Stats cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }} className="ind-stats-grid">
          {stats.map((s, i) => (
            <StatCountCard key={s.label} stat={s} color={color} active={visible} delay={i * 120}/>
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 640px) { .ind-stats-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function StatCountCard({ stat, color, active, delay }) {
  const { prefix, num, suffix, canAnim, isFloat } = parseStatVal(stat.val);
  const val = useCountUp(canAnim ? num : 0, active, 1600, isFloat);
  const display = canAnim
    ? `${prefix}${isFloat ? val.toFixed(1) : val}${suffix}`
    : stat.val;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: `${color}07`,
      border: `1px solid ${color}18`,
      borderRadius: 24,
      padding: 'clamp(28px,3.5vw,44px) clamp(20px,2.8vw,32px)',
      textAlign: 'center',
      opacity: active ? 1 : 0, transform: active ? 'none' : 'translateY(22px)',
      transition: `opacity 520ms ${delay}ms, transform 520ms ${delay}ms`,
    }}>
      {/* Background glow */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 100%, ${color}10 0%, transparent 65%)`, pointerEvents: 'none' }}/>
      {/* Top accent line */}
      <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 2, background: `linear-gradient(90deg, transparent, ${color}70, transparent)`, borderRadius: 1 }}/>

      {/* Number */}
      <div style={{
        fontFamily: "'Anton',sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(40px,6vw,68px)',
        color, lineHeight: 1, marginBottom: 12,
        position: 'relative',
      }}>
        {display}
      </div>

      {/* Label */}
      <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.52, fontWeight: 500, position: 'relative' }}>
        {stat.label}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   DELIVERABLES — entire section commented out
   Zone-X Delivers section
export function IndustryDeliverables({ items, color }) {
  const [ref, visible] = useRevealOnce();
  return (
    <section style={{ background: 'transparent', padding: 'clamp(48px,6vw,80px) 0' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>

        // Header
        <div ref={ref} style={{ marginBottom: 44, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)', transition: 'opacity 520ms, transform 520ms' }}>
          <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 10.5, color, textTransform: 'uppercase' }}>WHAT WE DELIVER</span>
          <h2 style={{ fontFamily: "'Anton',sans-serif", fontSize: 'clamp(28px,4vw,52px)', lineHeight: 1.07, marginTop: 8 }}>
            Zone-X Delivers
          </h2>
          <div style={{ width: visible ? 56 : 0, height: 3, background: color, borderRadius: 2, marginTop: 6, transition: 'width 600ms 200ms cubic-bezier(.2,.8,.2,1)', opacity: 0.75 }}/>
        </div>

        // Deliverables grid
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(10,10,20,0.08)', borderRadius: 24, overflow: 'hidden' }} className="deliver-list-grid">
          {items.map((item, i) => {
            const isLeft = i % 2 === 0;
            const isLastRow = i >= items.length - 2;
            return (
              <div key={i} style={{
                display: 'flex', gap: 16, alignItems: 'flex-start',
                padding: 'clamp(16px,2vw,22px) clamp(20px,2.4vw,32px)',
                borderBottom: isLastRow ? 'none' : '1px solid rgba(10,10,20,0.07)',
                borderRight: isLeft ? '1px solid rgba(10,10,20,0.07)' : 'none',
                opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(16px)',
                transition: `opacity 460ms ${i * 48 + 80}ms, transform 460ms ${i * 48 + 80}ms`,
              }}>
                // Number circle
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: `${color}12`, border: `1.5px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  fontFamily: "'Anton', sans-serif", fontSize: 9.5,                   color,
                }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <span style={{ fontSize: 14.5, lineHeight: 1.65, color: 'var(--ink)', fontWeight: 500, paddingTop: 4 }}>{item}</span>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) { .deliver-list-grid { grid-template-columns: 1fr !important; } .deliver-list-grid > div { border-right: none !important; } }
      `}</style>
    </section>
  );
}
══════════════════════════════════ */
export function IndustryDeliverables() { return null; }

/* ══════════════════════════════════
   CTA
══════════════════════════════════ */
export function IndustryCTA() {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding: 'clamp(40px,5vw,72px) 0 clamp(24px,3vw,40px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{
          background: 'linear-gradient(135deg, var(--ink) 0%, #1a1a2e 100%)',
          borderRadius: 28, padding: 'clamp(48px,6vw,88px) clamp(32px,5vw,80px)',
          textAlign: 'center', position: 'relative', overflow: 'hidden',
          opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)',
          transition: 'opacity 520ms, transform 520ms',
        }}>
          <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.5, background: 'radial-gradient(ellipse at 25% 50%, rgba(255,0,4,0.28), transparent 55%), radial-gradient(ellipse at 78% 50%, rgba(0,60,255,0.18), transparent 55%)' }}/>
          <div style={{ position: 'relative' }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>START YOUR PROJECT</span>
            <p style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 'clamp(28px,4.5vw,56px)', color: 'white', lineHeight: 1.2, margin: '14px 0 16px' }}>
              Ready to build your <span style={{ color: '#ff4d4d' }}>IoT solution?</span>
            </p>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 440, margin: '0 auto 40px' }}>
              Tell us about your project. Our team will get back to you quickly.
            </p>
            <Link to="/contact" className="hoverable" style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '17px 40px', background: 'linear-gradient(135deg, var(--red), #cc0003)', color: 'white', borderRadius: 999, fontSize: 14, fontWeight: 700,  boxShadow: '0 18px 48px rgba(255,0,4,0.38)', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 24px 56px rgba(255,0,4,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 18px 48px rgba(255,0,4,0.38)'; }}>
              Get In Touch <ArrowIcon size={13}/>
            </Link>
          </div>
        </div>
      </div>

      {/* Global keyframes for all industry page animations */}
      <style>{`
        @keyframes indFlow { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -18; } }
        @keyframes indNodePulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.1); opacity: 0.85; } }
        @keyframes indHeroOrb { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-20px) scale(1.08); } }
        @media (max-width: 860px) {
          .ind-hero-grid { grid-template-columns: 1fr !important; }
          .ind-hero-viz { display: none !important; }
        }
      `}</style>
    </section>
  );
}

/* ══════════════════════════════════
   USE CASE ICONS
══════════════════════════════════ */
export function UseCaseIcon({ icon, color, size = 18 }) {
  const s = size;
  const icons = {
    monitor: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke={color} strokeWidth="1.6"/><path d="M8 21h8M12 17v4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    hospital: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke={color} strokeWidth="1.6"/><path d="M9 22V12h6v10" stroke={color} strokeWidth="1.6"/></svg>,
    heart: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" stroke={color} strokeWidth="1.6"/></svg>,
    tag: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke={color} strokeWidth="1.6"/><circle cx="7" cy="7" r="1.5" fill={color}/></svg>,
    pill: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M10.5 20.5L3.5 13.5a5 5 0 017-7l7 7a5 5 0 01-7 7z" stroke={color} strokeWidth="1.6"/><line x1="8.5" y1="8.5" x2="15.5" y2="15.5" stroke={color} strokeWidth="1.6"/></svg>,
    thermometer: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z" stroke={color} strokeWidth="1.6"/></svg>,
    zap: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    sun: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="5" stroke={color} strokeWidth="1.6"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    droplet: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" stroke={color} strokeWidth="1.6"/></svg>,
    activity: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    alert: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={color} strokeWidth="1.6"/><line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="12" y1="17" x2="12.01" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>,
    bar: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><line x1="18" y1="20" x2="18" y2="10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="12" y1="20" x2="12" y2="4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="6" y1="20" x2="6" y2="14" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    truck: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="2" y="7" width="13" height="11" rx="1" stroke={color} strokeWidth="1.6"/><path d="M15 10h3.5L22 14v4h-7V10z" stroke={color} strokeWidth="1.6"/><circle cx="6.5" cy="18" r="2" stroke={color} strokeWidth="1.5"/><circle cx="17.5" cy="18" r="2" stroke={color} strokeWidth="1.5"/></svg>,
    snowflake: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><line x1="12" y1="2" x2="12" y2="22" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="2" y1="12" x2="22" y2="12" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="5.64" y1="5.64" x2="18.36" y2="18.36" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><line x1="18.36" y1="5.64" x2="5.64" y2="18.36" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    box: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke={color} strokeWidth="1.6"/></svg>,
    check: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6"/></svg>,
    map: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/><line x1="8" y1="2" x2="8" y2="18" stroke={color} strokeWidth="1.6"/><line x1="16" y1="6" x2="16" y2="22" stroke={color} strokeWidth="1.6"/></svg>,
    tool: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke={color} strokeWidth="1.6"/></svg>,
    cpu: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth="1.6"/><rect x="9" y="9" width="6" height="6" stroke={color} strokeWidth="1.6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    gauge: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12c0 2.76 1.12 5.26 2.93 7.07M22 12c0-2.76-1.12-5.26-2.93-7.07" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><circle cx="12" cy="12" r="1.5" fill={color}/><path d="M12 12l4-4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    wind: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9.59 4.59A2 2 0 1111 8H2M12.59 19.41A2 2 0 1014 16H2M17.73 7.73A2.5 2.5 0 1119.5 12H2" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    automation: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.6"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.64 5.64l2.12 2.12M16.24 16.24l2.12 2.12M5.64 18.36l2.12-2.12M16.24 7.76l2.12-2.12" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    dashboard: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="1.6"/></svg>,
    lightbulb: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9 21h6M12 3a6 6 0 016 6c0 2.5-1.5 4.5-3 6H9c-1.5-1.5-3-3.5-3-6a6 6 0 016-6z" stroke={color} strokeWidth="1.6"/></svg>,
    thermometer2: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z" stroke={color} strokeWidth="1.6"/></svg>,
    lock: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke={color} strokeWidth="1.6"/><path d="M7 11V7a5 5 0 0110 0v4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    camera: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke={color} strokeWidth="1.6"/><circle cx="12" cy="13" r="4" stroke={color} strokeWidth="1.6"/></svg>,
    plug: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M18 7c0-2.21-2.69-4-6-4S6 4.79 6 7M18 7H6M18 7v3M6 7v3M2 10h20M7 10v10a1 1 0 001 1h8a1 1 0 001-1V10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    bell: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    users: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke={color} strokeWidth="1.6"/><circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.6"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    book: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke={color} strokeWidth="1.6"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke={color} strokeWidth="1.6"/></svg>,
    energy: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2v6M6 6l4 4M18 6l-4 4M12 22v-6M6 18l4-4M18 18l-4-4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.6"/></svg>,
    bus: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M8 6v6M16 6v6M2 12h20M4 18h.01M20 18h.01M4 6h16a1 1 0 011 1v11H3V7a1 1 0 011-1z" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    cloud: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    graduation: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M22 10L12 5 2 10l10 5 10-5z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/></svg>,
  };
  return icons[icon] || icons['check'];
}

export function Footer() { return <FooterBase hideCTA/>; }
